import { getAiToolStudioConfig } from '@/pages/ipe/workbench/constants/aiStudioConfig';
import i18n from '@/i18n';
import { getModelStudioConfig } from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { getImageFissionStudioConfig } from '@/pages/ipe/workbench/constants/imageFissionStudioConfig';
import { getImg2ImgOutputs } from './img2imgTaskResult';
import { resolveAiCopywritingBatchNo, resolveImg2ImgBatchNo, resolveAiTextToImageBatchNo, resolveAiOutpaintBatchNo, resolveImageCropBatchNo, resolveImageEnhanceBatchNo, resolveSmartCutoutBatchNo, resolveStampExtractBatchNo, resolveModelTryOnBatchNo, resolveModelHandheldBatchNo, resolveModelPoseFissionBatchNo, resolveModelUniversalWearBatchNo } from './taskIdGenerator';
import { retryImageProcessStudioTask } from './imageProcessStudioTaskRetry';
import { cancelImg2ImgTask } from './img2imgTaskCancel';
import { cancelAiCopywritingTask } from './aiCopywritingTaskCancel';
import { getAiCopywritingTaskCompletionDisplay, getCropTaskCompletionDisplay, getGoodsTaskCompletionDisplay, getImg2ImgTaskCompletionDisplay, getFissionTaskCompletionDisplay } from './taskProgress';
import { cancelVideoTask } from './videoTaskCancel';
import { retryVideoTask } from './videoTaskRetry';
import { getImageProcessStudioTaskCompletionDisplay } from './imageProcessStudioTaskProgress';
import { isImageProcessStudioNav } from '@/pages/ipe/workbench/constants/imageProcessStudioConfig';
import { retryAiCopywritingTask } from './aiCopywritingTaskRetry';
import { retryImg2imgTask } from './img2imgTaskRetry';
import { retryAiOutpaintTask, retryAiTextToImageTask } from './aiStudioImageTaskRetry';
import {
  retryModelHandheldTask,
  retryModelPoseFissionTask,
  retryModelTryOnTask,
  retryModelUniversalWearTask,
} from './modelStudioTaskRetry';

import { resolveCopywritingLanguageInfo, localizeCopywritingLanguageLabel } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  formatFissionBatchAsTask,
  resolveFissionBatchNo,
  resolveFissionHistoryBadgeTaskId,
} from './imageFissionTaskHistory';

function formatCopywritingTask(task, { resolveBatchNo = resolveAiStudioBatchNo } = {}) {
  const formatted = formatAiStudioTask(task, { resolveBatchNo });
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const content = String(r.content || r.text || '').trim();
  const refPreview = Array.isArray(r.referencePreviews) && r.referencePreviews[0]
    ? r.referencePreviews[0]
    : (Array.isArray(r.referenceUrls) && r.referenceUrls[0] ? r.referenceUrls[0] : '');
  const { label: languageLabel } = resolveCopywritingLanguageInfo(r);
  formatted._promptPreview = r.prompt ? String(r.prompt) : content.slice(0, 120);
  formatted._outputPreview = refPreview;
  formatted.previewUrl = refPreview;
  formatted._contentPreview = content.slice(0, 200);
  formatted._languageLabel = localizeCopywritingLanguageLabel(languageLabel);
  return formatted;
}

export function resolveAiStudioBatchNo(task) {
  const sub = String(task?.subAccount || task?.name || '').trim();
  if (sub) return sub;
  return String(task?.id || task?.taskId || '').trim();
}

export function formatAiStudioTask(task, { resolveBatchNo = resolveAiStudioBatchNo } = {}) {
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  let outputs = getImg2ImgOutputs(r);
  if (outputs.length === 0 && Array.isArray(r.outputs)) {
    outputs = r.outputs;
  }
  const firstOutput = outputs.find((o) => (o?.outputUrl || o?.url || o?.imageUrl) && o.status !== 'failed');
  const preview = firstOutput?.outputUrl || firstOutput?.url || firstOutput?.imageUrl
    || r.outputUrl
    || (Array.isArray(r.urls) ? r.urls[0] : '')
    || r.previewUrl
    || r.imageUrl
    || task.previewUrl
    || task.imageUrl
    || '';
  const previewViewUrl = firstOutput?.viewUrl
    || firstOutput?.displayUrl
    || r.viewUrl
    || r.displayUrl
    || task.previewViewUrl
    || '';
  const totalRaw = Number(task?.total);
  const total = Number.isFinite(totalRaw) && totalRaw > 0
    ? totalRaw
    : Math.max(outputs.length, 1);
  const savedOperator = task?.operator || task?.createdBy || task?.creator
    || r.operator || r.operatorName || '';
  return {
    ...task,
    id: task.id || task._id?.toString() || task._id,
    subAccount: resolveBatchNo(task),
    operator: savedOperator,
    createdBy: savedOperator,
    total,
    successCount: task.successCount ?? r.successCount ?? outputs.filter((o) => o.status === 'success').length,
    failedCount: task.failedCount ?? r.failedCount ?? outputs.filter((o) => o.status === 'failed').length,
    _promptPreview: r.prompt ? String(r.prompt) : '',
    _outputPreview: preview,
    previewUrl: preview,
    previewViewUrl,
    previewDisplayUrl: previewViewUrl || firstOutput?.displayUrl || r.displayUrl || '',
  };
}

/** 文生/图生视频历史：优先成片首帧 poster，其次参考图 */
export function formatVideoStudioHistoryTask(task, { resolveBatchNo = resolveAiStudioBatchNo } = {}) {
  const base = formatAiStudioTask(task, { resolveBatchNo });
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const refPreview = Array.isArray(r.referencePreviews)
    ? String(r.referencePreviews.find((u) => String(u || '').trim()) || '').trim()
    : '';
  const preview = String(
    r.outputVideoPosterUrl
    || r.previewUrl
    || r.outputTryOnImageUrl
    || refPreview
    || r.firstFrameUrl
    || r.referenceImageUrl
    || base.previewUrl
    || '',
  ).trim();
  const prompt = String(
    r.supplementPrompt
    || r.scriptPrompt
    || r.customPrompt
    || r.prompt
    || base._promptPreview
    || '',
  ).trim();
  return {
    ...base,
    _promptPreview: prompt,
    _outputPreview: preview,
    previewUrl: preview,
    // 仅保留已签发展示地址；勿把裸 /uploads canonical 塞进 previewDisplayUrl（线下会当 img src → 404）
    previewViewUrl: base.previewViewUrl || '',
    previewDisplayUrl: base.previewDisplayUrl || '',
  };
}

function buildFilterKeyword(task, batchNo) {
  return `${batchNo} ${task._promptPreview || ''} ${task.id || ''}`.toLowerCase();
}

function buildFissionHistoryConfig(navId) {
  const isPattern = navId === 'patternFission';
  return {
    dataSource: 'fission',
    badgeScopeId: 'imageFission',
    toolLabel: isPattern ? '图案裂变' : '图裂变',
    emptyDescription: '提交裂变任务后，记录将自动保存在这里',
    resolveBatchNo: resolveFissionBatchNo,
    getCompletionDisplay: getFissionTaskCompletionDisplay,
    formatTask: formatFissionBatchAsTask,
    detailPath: (batchKey) => `/ipe/image-fission-detail/${encodeURIComponent(batchKey)}`,
    getDetailNavigateState: (task) => ({ batch: task._rawBatch || null }),
    resolveHistoryBadgeTaskId: resolveFissionHistoryBadgeTaskId,
    skipServerBatchCancel: true,
    getTaskPreviewUrl: (task) => task.previewUrl || '',
  };
}

function buildImageCropDetailPath(taskId) {
  return `/ipe/image-crop-detail/${encodeURIComponent(taskId)}`;
}

function buildImageCropDetailNavigateState(task) {
  return {
    task,
    imageIds: task?.imageIds || task?.result?.imageIds || [],
  };
}

function formatImageCropHistoryTask(task) {
  const formatted = formatAiStudioTask(task, { resolveBatchNo: resolveImageCropBatchNo });
  const { resolveCropPreviewFromTaskResult } = require('@/image-crop');
  const { isLoadableDisplayPreviewUrl } = require('@/utils/imageDisplayAccess');
  const inlinePreview = resolveCropPreviewFromTaskResult(task);
  if (inlinePreview) {
    formatted.previewUrl = inlinePreview;
    formatted._outputPreview = inlinePreview;
    if (!formatted.previewDisplayUrl && !formatted.previewViewUrl) {
      const loadable = isLoadableDisplayPreviewUrl(inlinePreview) ? inlinePreview : '';
      if (loadable) {
        formatted.previewDisplayUrl = loadable;
        formatted.previewViewUrl = loadable;
      }
    }
  }
  return formatted;
}

function cancelImageCropTaskEverywhere(taskId) {
  const { cancelImageCropTask, stopImageCropTaskInBackground } = require('@/image-crop');
  const { cancelIpeServerBatch } = require('@/pages/ipe/workbench/utils/ipeBatchTaskCancel');
  cancelImageCropTask(taskId);
  stopImageCropTaskInBackground(taskId);
  return cancelIpeServerBatch(taskId);
}

export function resolveImageCropTaskRecordId(task) {
  return String(task?.taskId || task?.id || '').trim();
}

function buildAiImageDetailPath(navId) {
  return (batchKey) => `/ipe/ai-image-detail/${encodeURIComponent(navId)}/${encodeURIComponent(batchKey)}`;
}

function buildAiImageDetailNavigateState(navId) {
  return (task) => ({ task, navId });
}

function buildAiModelDetailPath(navId) {
  return (batchKey) => `/ipe/ai-model-detail/${encodeURIComponent(navId)}/${encodeURIComponent(batchKey)}`;
}

function buildAiModelDetailNavigateState(navId) {
  return (task) => ({ task, navId });
}

const BASE_CONFIGS = {
  img2img: {
    taskType: 'img2img',
    toolLabel: '万能改图',
    resolveBatchNo: resolveImg2ImgBatchNo,
    cancelBeforeServer: cancelImg2ImgTask,
    detailPath: buildAiImageDetailPath('img2img'),
    getDetailNavigateState: buildAiImageDetailNavigateState('img2img'),
    buildFilterKeyword: (task, batchNo) => `${batchNo} ${task._promptPreview || ''}`.toLowerCase(),
    retryTask: retryImg2imgTask,
    retryToast: '改图已重新提交到队列',
  },
  aiOutpaint: {
    taskType: 'aiOutpaint',
    toolLabel: 'AI扩图',
    emptyDescription: '扩图生成后，记录将自动保存在这里',
    resolveBatchNo: resolveAiOutpaintBatchNo,
    retryTask: retryAiOutpaintTask,
    retryToast: '扩图已重新提交到队列',
    detailPath: buildAiImageDetailPath('aiOutpaint'),
    getDetailNavigateState: buildAiImageDetailNavigateState('aiOutpaint'),
  },
  imageCrop: {
    taskType: 'imageCrop',
    toolLabel: '图案裁剪',
    emptyDescription: '图案裁剪完成后，记录将自动保存在这里',
    resolveBatchNo: resolveImageCropBatchNo,
    skipServerBatchCancel: true,
    cancelBeforeServer: cancelImageCropTaskEverywhere,
    retryTask: (task) => {
      const { retryImageCropStudioTask } = require('@/image-crop');
      return retryImageCropStudioTask(task);
    },
    retryToast: '图案裁剪已重新提交',
    detailPath: buildImageCropDetailPath,
    getDetailNavigateState: buildImageCropDetailNavigateState,
    getCompletionDisplay: getCropTaskCompletionDisplay,
    formatTask: formatImageCropHistoryTask,
    getTaskPreviewUrl: (task) => task._outputPreview || task.previewUrl || '',
  },
  imageEnhance: {
    taskType: 'imageEnhance',
    toolLabel: '超分提质',
    emptyDescription: '放大完成后，记录将自动保存在这里',
    resolveBatchNo: resolveImageEnhanceBatchNo,
    cancelBeforeServer: (taskId) => {
      const { cancelImageEnhanceTask } = require('@/pages/ipe/workbench/utils/imageEnhanceTaskCancel');
      cancelImageEnhanceTask(taskId);
    },
    retryTask: (task) => retryImageProcessStudioTask('imageEnhance', task),
    retryToast: '超分提质已重新提交',
    detailPath: buildAiImageDetailPath('imageEnhance'),
    getDetailNavigateState: buildAiImageDetailNavigateState('imageEnhance'),
  },
  smartCutout: {
    taskType: 'smartCutout',
    toolLabel: '智能抠图',
    emptyDescription: '抠图完成后，记录将自动保存在这里',
    resolveBatchNo: resolveSmartCutoutBatchNo,
    cancelBeforeServer: (taskId) => {
      const { cancelSmartCutoutTask } = require('@/pages/ipe/workbench/utils/smartCutoutTaskCancel');
      cancelSmartCutoutTask(taskId);
    },
    retryTask: (task) => retryImageProcessStudioTask('smartCutout', task),
    retryToast: '智能抠图已重新提交',
    detailPath: buildAiImageDetailPath('smartCutout'),
    getDetailNavigateState: buildAiImageDetailNavigateState('smartCutout'),
  },
  stampExtract: {
    taskType: 'stampExtract',
    toolLabel: '印花提取',
    emptyDescription: '印花提取完成后，记录将自动保存在这里',
    resolveBatchNo: resolveStampExtractBatchNo,
    retryTask: (task) => retryImageProcessStudioTask('stampExtract', task),
    retryToast: '印花提取已重新提交',
    detailPath: buildAiImageDetailPath('stampExtract'),
    getDetailNavigateState: buildAiImageDetailNavigateState('stampExtract'),
  },
  aiTextToImage: {
    taskType: 'aiTextToImage',
    toolLabel: '文生图',
    emptyDescription: '文生图生成后，记录将自动保存在这里',
    resolveBatchNo: resolveAiTextToImageBatchNo,
    retryTask: retryAiTextToImageTask,
    retryToast: '文生图已重新提交到队列',
    detailPath: buildAiImageDetailPath('aiTextToImage'),
    getDetailNavigateState: buildAiImageDetailNavigateState('aiTextToImage'),
  },
  aiCopywriting: {
    taskType: 'aiCopywriting',
    toolLabel: 'AI文案',
    emptyDescription: '文案生成后，记录将自动保存在这里',
    resolveBatchNo: resolveAiCopywritingBatchNo,
    detailPath: buildAiImageDetailPath('aiCopywriting'),
    getDetailNavigateState: buildAiImageDetailNavigateState('aiCopywriting'),
    getCompletionDisplay: getAiCopywritingTaskCompletionDisplay,
    formatTask: (task) => formatCopywritingTask(task, { resolveBatchNo: resolveAiCopywritingBatchNo }),
    getTaskPreviewUrl: (task) => task._outputPreview || task.previewUrl || '',
    cancelBeforeServer: cancelAiCopywritingTask,
    retryTask: retryAiCopywritingTask,
    retryToast: '文案已重新提交到队列',
  },
  modelTryOn: {
    taskType: 'modelTryOn',
    toolLabel: '模特试衣',
    resolveBatchNo: resolveModelTryOnBatchNo,
    detailPath: buildAiModelDetailPath('modelTryOn'),
    getDetailNavigateState: buildAiModelDetailNavigateState('modelTryOn'),
    retryTask: retryModelTryOnTask,
    retryToast: '试衣已重新提交到队列',
  },
  modelHandheld: {
    taskType: 'modelHandheld',
    toolLabel: '手持商品',
    resolveBatchNo: resolveModelHandheldBatchNo,
    detailPath: buildAiModelDetailPath('modelHandheld'),
    getDetailNavigateState: buildAiModelDetailNavigateState('modelHandheld'),
    retryTask: retryModelHandheldTask,
    retryToast: '手持商品已重新提交到队列',
  },
  modelPoseFission: {
    taskType: 'modelPoseFission',
    toolLabel: '姿势裂变',
    resolveBatchNo: resolveModelPoseFissionBatchNo,
    detailPath: buildAiModelDetailPath('modelPoseFission'),
    getDetailNavigateState: buildAiModelDetailNavigateState('modelPoseFission'),
    retryTask: retryModelPoseFissionTask,
    retryToast: '姿势裂变已重新提交到队列',
  },
  modelUniversalWear: {
    taskType: 'modelUniversalWear',
    toolLabel: '万物穿戴',
    resolveBatchNo: resolveModelUniversalWearBatchNo,
    detailPath: buildAiModelDetailPath('modelUniversalWear'),
    getDetailNavigateState: buildAiModelDetailNavigateState('modelUniversalWear'),
    retryTask: retryModelUniversalWearTask,
    retryToast: '万物穿戴已重新提交到队列',
  },
  videos: {
    taskType: 'videos',
    toolLabel: '图生视频',
    emptyDescription: '图生视频生成后，记录将自动保存在这里',
    detailPath: (batchKey) => `/ipe/video-generate-detail/${encodeURIComponent(batchKey)}`,
    cancelBeforeServer: cancelVideoTask,
    skipServerBatchCancel: true,
    /** cancel-video 已写入终态，勿再 taskAPI.update 覆盖为 partial */
    cancelCompletesOnServer: true,
    retryTask: retryVideoTask,
    retryToast: '任务已重新开始',
    getCompletionDisplay: getGoodsTaskCompletionDisplay,
    formatTask: (task) => formatVideoStudioHistoryTask(task, {
      resolveBatchNo: (t) => resolveAiStudioBatchNo(t),
    }),
    buildFilterKeyword: (task, batchNo) => {
      const r = task?.result && typeof task.result === 'object' ? task.result : {};
      const prompt = r.supplementPrompt || r.conceptPresetId || r.customPrompt || r.scriptPrompt || '';
      return `${batchNo} ${prompt} ${task.id || ''}`.toLowerCase();
    },
  },
  textToVideo: {
    taskType: 'textToVideo',
    toolLabel: '文生视频',
    emptyDescription: '文生视频生成后，记录将自动保存在这里',
    detailPath: (batchKey) => `/ipe/video-generate-detail/${encodeURIComponent(batchKey)}`,
    cancelBeforeServer: cancelVideoTask,
    skipServerBatchCancel: true,
    cancelCompletesOnServer: true,
    retryTask: retryVideoTask,
    retryToast: '任务已重新开始',
    getCompletionDisplay: getGoodsTaskCompletionDisplay,
    formatTask: (task) => formatVideoStudioHistoryTask(task, {
      resolveBatchNo: (t) => resolveAiStudioBatchNo(t),
    }),
    buildFilterKeyword: (task, batchNo) => {
      const r = task?.result && typeof task.result === 'object' ? task.result : {};
      const prompt = r.scriptPrompt || r.customPrompt || r.prompt || '';
      return `${batchNo} ${prompt} ${task.id || ''}`.toLowerCase();
    },
  },
  imageFission: buildFissionHistoryConfig('imageFission'),
  patternFission: buildFissionHistoryConfig('patternFission'),
};

const HISTORY_RETRY_STYLE = {
  img2img: 'queued',
  aiOutpaint: 'queued',
  aiTextToImage: 'queued',
  aiCopywriting: 'queued',
  modelTryOn: 'queued',
  modelHandheld: 'queued',
  modelPoseFission: 'queued',
  modelUniversalWear: 'queued',
  imageCrop: 'resubmitted',
  imageEnhance: 'resubmitted',
  smartCutout: 'resubmitted',
  stampExtract: 'resubmitted',
  videos: 'restarted',
  textToVideo: 'restarted',
};

function resolveHistoryEmptyDescription(navId, fallback) {
  const key = navId === 'imageFission' || navId === 'patternFission' ? 'fission' : navId;
  return i18n.t(`workbench:historyPanel.empty.${key}`, {
    defaultValue: fallback || i18n.t('workbench:historyPanel.emptyGeneric'),
  });
}

function resolveHistoryRetryToast(navId, toolLabel, fallback) {
  const style = HISTORY_RETRY_STYLE[navId];
  if (style === 'queued') {
    return i18n.t('workbench:historyPanel.retryQueued', { tool: toolLabel });
  }
  if (style === 'resubmitted') {
    return i18n.t('workbench:historyPanel.retryResubmitted', { tool: toolLabel });
  }
  if (style === 'restarted') {
    return i18n.t('workbench:historyPanel.retryRestarted');
  }
  return fallback || i18n.t('workbench:historyPanel.retryGeneric');
}

export function getAiStudioHistoryConfig(navId) {
  const fissionStudio = navId === 'imageFission' || navId === 'patternFission'
    ? getImageFissionStudioConfig(navId)
    : null;
  const studio = getAiToolStudioConfig(navId) || getModelStudioConfig(navId) || fissionStudio;
  const base = BASE_CONFIGS[navId] || {};
  const resolveBatchNo = base.resolveBatchNo || resolveAiStudioBatchNo;
  const toolLabel = i18n.t(`workbench:nav.${navId}.label`, {
    defaultValue: base.toolLabel || studio?.title || i18n.t('workbench:historyPanel.taskFallback'),
  });
  return {
    dataSource: base.dataSource || 'task',
    badgeScopeId: base.badgeScopeId || navId,
    taskType: base.taskType || navId,
    toolLabel,
    emptyDescription: resolveHistoryEmptyDescription(
      navId,
      base.emptyDescription || studio?.historyEmptyDescription,
    ),
    resolveBatchNo,
    cancelBeforeServer: base.cancelBeforeServer || null,
    skipServerBatchCancel: Boolean(base.skipServerBatchCancel),
    cancelCompletesOnServer: Boolean(base.cancelCompletesOnServer),
    detailPath: base.detailPath || null,
    getDetailNavigateState: base.getDetailNavigateState || null,
    resolveHistoryBadgeTaskId: base.resolveHistoryBadgeTaskId || null,
    buildFilterKeyword: base.buildFilterKeyword || buildFilterKeyword,
    retryToast: resolveHistoryRetryToast(navId, toolLabel, base.retryToast),
    getCompletionDisplay: base.getCompletionDisplay
      || (isImageProcessStudioNav(navId)
        ? (task) => getImageProcessStudioTaskCompletionDisplay(task, navId)
        : getImg2ImgTaskCompletionDisplay),
    formatTask: base.formatTask || ((task) => formatAiStudioTask(task, { resolveBatchNo })),
    retryTask: base.retryTask || null,
    getTaskPreviewUrl: base.getTaskPreviewUrl
      || ((task) => task._outputPreview || task.previewUrl || ''),
  };
}
